import {Component, inject, Injector, OnInit, signal} from "@angular/core";
import {
	BasicMaterialDialog,
	MatDialogDataBaseInput,
	MatDialogDataBaseOutput,
} from "../../basic-material-dialog/basic-material-dialog.directive";
import {FetchDataType, SimGroup, SimUser} from "@models";
import {CreateGroupDialogComponent} from "../../new-item-dialogs/group/new-group-dialog/create-group-dialog.component";
import {NonNullableFormBuilder, Validators} from "@angular/forms";
import {SimGroupingService} from "@services";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {CustomColorMapping} from "../../../../../shared/models/SimEventPalette";

export interface DialogData_AddGroupMembersInput extends MatDialogDataBaseInput {
	subgroups: SimGroup[];
	preselectSubgroup: SimGroup | null;
	parentGroup: SimGroup;
}

export interface DialogData_AddGroupMembersOutput extends MatDialogDataBaseOutput {
	group: SimGroup;
}

@UntilDestroy()
@Component({
	selector: "sim-add-group-members-dialog",
	templateUrl: "./add-group-members-dialog.component.html",
	styleUrl: "./add-group-members-dialog.component.scss",
	standalone: false,
})
export class AddGroupMembersDialogComponent
	extends BasicMaterialDialog<CreateGroupDialogComponent, DialogData_AddGroupMembersInput, DialogData_AddGroupMembersOutput>
	implements OnInit
{
	// Properties
	public promise: Promise<unknown> | null = null;
	public subgroups = signal<SimGroup[]>([...this.data.subgroups]);
	public selectedSubgroup = signal<SimGroup | null>(this.data.preselectSubgroup);
	public excludeIds = signal<string[]>([]);

	// Readonly
	protected readonly FetchDataType = FetchDataType;
	protected readonly CustomColorMapping = CustomColorMapping;

	// Form
	private _formBuilder = inject(NonNullableFormBuilder);
	public form = this._formBuilder.group({
		members: this._formBuilder.control<SimUser[]>([], Validators.required),
		assignedGroupId: this._formBuilder.control(this.data.preselectSubgroup?.id ?? this.data.parentGroup.id),
	});

	// Injections
	private _groupService = inject(SimGroupingService);

	constructor(_injector: Injector) {
		super(_injector);
	}

	public ngOnInit(): void {
		const previousMemberIds = [...this.data.parentGroup.memberIds, ...this.data.subgroups.flatMap((group) => group.memberIds)];
		this.excludeIds.set(previousMemberIds);
		this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(({members, assignedGroupId}) => {
			// Members
			if (members) {
				this.excludeIds.set([...previousMemberIds, ...this.data.parentGroup.memberIds, ...members.map((member) => member.id)]);
			}

			// Group
			if (assignedGroupId) {
				// Parent Group
				if (assignedGroupId === this.data.parentGroup.id) {
					this.selectedSubgroup.set(null);
					return;
				}

				// Subgroup
				const subgroup = this.subgroups().find((group) => group.id === assignedGroupId);
				this.selectedSubgroup.set(subgroup ?? null);
			}
		});
	}

	protected doSubmit(): void {
		// Validate
		if (this.form.invalid) {
			return;
		}

		// Send request
		// raw value because form fields can not be disabled.
		const rawValue = this.form.getRawValue();
		const request = {
			simGroupId: rawValue.assignedGroupId,
			memberIds: rawValue.members.map((member) => member.id),
		};
		this.promise = this._groupService.addMembersToSimGroup(request).then((group) => {
			this.close({action: "ok", group});
		});
	}

	protected addMember(member: SimUser | null): void {
		if (!member) {
			return;
		}
		const members = [...this.form.controls.members.getRawValue(), member];
		this.form.patchValue({members});
	}

	protected removeMember(member: SimUser): void {
		const members = this.form.controls.members.getRawValue().filter((m) => m.id !== member.id);
		this.form.patchValue({members});
	}
}
